import { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router';
import {
  ArrowLeft, Clock, MapPin, Bookmark, Share2,
  FileText, Zap, Loader2, BookOpen, Users,
} from 'lucide-react';
import { notify } from '../utils/notifications';
import { AvatarImage } from '../components/AvatarImage';
import { useEventData } from '../context/EventDataContext';
import { useFavorites } from '../hooks/useFavorites';
import { isSessionPrepEnabled, type FeedbackAvailabilityState, type Session } from '@conference/shared';
import { CopilotFab } from '../components/CopilotFab';
import { useEventModules } from '../hooks/useEventModules';
import { useApp } from '../context/AppContext';
import { sessionTypeLabel } from '../utils/schedule';
import { sessionFeedbackService } from '../services/sessionFeedbackService';

// API also returns linked papers (added by our backend update)
type SessionWithPapers = Session & {
  papers?: { id: string; title: string; authors?: string[]; paper_number?: string; processing_status?: string; presentation_topic?: string; presentation_start_time?: string; presentation_end_time?: string }[];
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const TRACK_STYLES: Record<string, { bg: string; text: string; accent: string }> = {
  keynote:  { bg: 'rgba(14,165,233,0.12)',  text: '#38BDF8', accent: '#0EA5E9' },
  talk:     { bg: 'rgba(124,58,237,0.12)',  text: '#A78BFA', accent: '#7C3AED' },
  workshop: { bg: 'rgba(5,150,105,0.12)',   text: '#34D399', accent: '#059669' },
  panel:    { bg: 'rgba(217,119,6,0.12)',   text: '#FBBF24', accent: '#D97706' },
  poster:   { bg: 'rgba(239,68,68,0.12)',   text: '#F87171', accent: '#EF4444' },
  break:    { bg: 'rgba(100,116,139,0.12)', text: '#94A3B8', accent: '#64748B' },
  social:   { bg: 'rgba(236,72,153,0.12)',  text: '#F472B6', accent: '#EC4899' },
};

const DEFAULT_STYLE = { bg: 'rgba(100,116,139,0.12)', text: '#94A3B8', accent: '#64748B' };

function trackStyle(type: string) {
  return TRACK_STYLES[type?.toLowerCase()] ?? DEFAULT_STYLE;
}

function fmt(t: string): string {
  if (!t) return '';
  const [h, m] = t.split(':').map(Number);
  const ampm = h >= 12 ? 'PM' : 'AM';
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${ampm}`;
}

function fmtDate(d: string): string {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-US', {
    weekday: 'long', month: 'long', day: 'numeric',
  });
}

function durationLabel(start: string, end: string): string {
  if (!start || !end) return '';
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const mins = (eh * 60 + em) - (sh * 60 + sm);
  if (mins <= 0) return '';
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}m` : `${h}h`;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function SessionDetailScreen() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { toggleFavorite, isFavorite } = useFavorites();
  const { currentEventId, selectedEvent } = useApp();
  const { isEnabled } = useEventModules();
  const { preview, services: { sessionService } } = useEventData();

  const [session, setSession] = useState<SessionWithPapers | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [presentationFeedbackState, setPresentationFeedbackState] = useState<FeedbackAvailabilityState | null>(null);

  const saved = isFavorite(id ?? '', 'session');
  const prepAIEnabled = isEnabled('ai_copilot') && isSessionPrepEnabled(
    selectedEvent?.app_modules,
    session?.type,
  );

  useEffect(() => {
    if (!id || !currentEventId) { setNotFound(true); setLoading(false); return; }
    setLoading(true);
    sessionService.getById(id, currentEventId)
      .then((data) => {
        if (!data) setNotFound(true);
        else setSession(data as SessionWithPapers);
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [currentEventId, id, sessionService]);

  const feedbackPaperId = session?.papers?.[0]?.id;
  useEffect(() => {
    if (preview) { setPresentationFeedbackState('unavailable'); return; }
    if (!feedbackPaperId) { setPresentationFeedbackState(null); return; }
    let disposed = false;
    sessionFeedbackService.getPresentationForm(feedbackPaperId)
      .then((form) => { if (!disposed) setPresentationFeedbackState(form.state); })
      .catch(() => { if (!disposed) setPresentationFeedbackState('unavailable'); });
    return () => { disposed = true; };
  }, [feedbackPaperId, preview]);

  const handleSave = () => {
    if (!id) return;
    const wasSaved = isFavorite(id, 'session');
    toggleFavorite(id, 'session');
    if (!wasSaved) notify.custom.sessionSaved();
    else notify.info('Removed from schedule');
  };

  // ── Loading ──────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="size-full flex items-center justify-center" style={{ background: 'var(--app-bg)' }}>
        <Loader2 className="w-7 h-7 animate-spin" style={{ color: 'var(--app-accent)' }} />
      </div>
    );
  }

  // ── Not found ────────────────────────────────────────────────────────────────
  if (notFound || !session) {
    return (
      <div className="size-full flex flex-col items-center justify-center gap-3 px-8" style={{ background: 'var(--app-bg)' }}>
        <button
          aria-label="Back"
          onClick={() => navigate(-1)}
          className="absolute top-12 left-4 size-10 rounded-full flex items-center justify-center"
          style={{ background: 'var(--app-surface)' }}
        >
          <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
        </button>
        <p className="text-sm text-center" style={{ color: 'var(--app-text-muted)' }}>Session not found</p>
        <button className="text-xs underline" style={{ color: 'var(--app-accent)' }} onClick={() => navigate(-1)}>
          Go back
        </button>
      </div>
    );
  }

  // ── Data helpers ─────────────────────────────────────────────────────────────
  const selectedTopic = searchParams.get('topic');
  const papers = selectedTopic ? session.papers?.filter((paper) => paper.presentation_topic === selectedTopic) ?? [] : session.papers ?? [];
  const startTime = papers[0]?.presentation_start_time ?? session.start_time;
  const endTime = papers.at(-1)?.presentation_end_time ?? session.end_time;
  const agendaFilterOverrides = selectedEvent?.id === currentEventId ? selectedEvent.app_modules?.agenda?.filters ?? undefined : undefined;
  const sessionLabel = sessionTypeLabel(session.type, agendaFilterOverrides);
  const ts = trackStyle(session.type);
  const dur = durationLabel(startTime, endTime);

  // API returns SpeakerBrief directly; handle both shapes defensively
  const speakers = (session.speakers ?? []) as Array<{
    id?: string;
    name?: string;
    title?: string;
    company?: string;
    avatar_url?: string;
    profile?: { id: string; name: string; title?: string; company?: string; avatar_url?: string };
  }>;

  // ── Render ───────────────────────────────────────────────────────────────────
  return (
    <div className="size-full flex flex-col overflow-hidden" style={{ background: 'var(--app-bg)' }}>

      {/* ── Coloured header ─────────────────────────────────────────────────── */}
      <div
        className="app-screen-header shrink-0 px-4 pt-12 pb-6 border-b"
        style={{
          background: `linear-gradient(160deg, ${ts.bg} 0%, var(--app-bg) 100%)`,
          borderColor: 'var(--app-divider)',
        }}
      >
        {/* Top row: back + actions */}
        <div className="flex items-center justify-between mb-5">
          <button
            aria-label="Back"
            onClick={() => navigate(-1)}
            className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
            style={{ background: 'var(--app-surface)' }}
          >
            <ArrowLeft className="w-5 h-5" style={{ color: 'var(--app-text)' }} />
          </button>
          {!preview && <div className="flex gap-2">
            <button
              onClick={() => notify.success('Link copied to clipboard')}
              className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
              style={{ background: 'var(--app-surface)' }}
            >
              <Share2 className="w-4 h-4" style={{ color: 'var(--app-text-subtle)' }} />
            </button>
            <button
              onClick={handleSave}
              className="size-10 rounded-full flex items-center justify-center active:scale-90 transition-transform"
              style={{ background: saved ? ts.bg : 'var(--app-surface)', borderWidth: 1, borderColor: saved ? ts.accent : 'transparent' }}
            >
              <Bookmark
                className="w-4 h-4"
                style={{ color: saved ? ts.text : 'var(--app-text-subtle)' }}
                fill={saved ? ts.text : 'none'}
              />
            </button>
          </div>}
        </div>

        {/* Type badge + live indicator */}
        <div className="flex items-center gap-2 mb-2">
          <span
            className="text-[11px] px-2.5 py-0.5 rounded-full capitalize"
            style={{ background: ts.bg, color: ts.text }}
          >
            {sessionLabel}
          </span>
          {session.is_featured && (
            <span
              className="text-[11px] px-2.5 py-0.5 rounded-full"
              style={{ background: 'rgba(245,158,11,0.15)', color: '#FBBF24' }}
            >
              Featured
            </span>
          )}
          {session.status === 'live' && (
            <div className="flex items-center gap-1">
              <span className="size-1.5 rounded-full bg-[#ef4444] animate-pulse" />
              <span className="text-[11px] text-[#ef4444]">Live now</span>
            </div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-[22px] leading-snug mb-1" style={{ color: 'var(--app-text)' }}>
          {selectedTopic && papers.length ? selectedTopic : session.title}
        </h1>

        {selectedTopic && papers.length && <p className="text-sm" style={{ color: 'var(--app-text-muted)' }}>{session.title}</p>}
        {session.track && (
          <p className="text-sm" style={{ color: ts.text }}>{session.track}</p>
        )}
      </div>

      {/* ── Scrollable body ──────────────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto px-4 pt-4 pb-10">

        {/* Time & Location card */}
        <div
          className="rounded-xl p-4 mb-4 flex flex-col gap-3 border"
          style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
        >
          <div className="flex items-start gap-3">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" style={{ color: ts.text }} />
            <div>
              <p className="text-sm" style={{ color: 'var(--app-text)' }}>
                {fmt(startTime)} – {fmt(endTime)}
                {dur && (
                  <span className="text-xs ml-2" style={{ color: 'var(--app-text-muted)' }}>({dur})</span>
                )}
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                {fmtDate(session.date)}
              </p>
            </div>
          </div>
          {session.room && (
            <div className="flex items-center gap-3">
              <MapPin className="w-4 h-4 shrink-0" style={{ color: ts.text }} />
              <p className="text-sm" style={{ color: 'var(--app-text)' }}>{session.room}</p>
            </div>
          )}
        </div>

        {/* Tags */}
        {session.tags && session.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-4">
            {session.tags.map((tag) => (
              <span
                key={tag}
                className="text-xs px-3 py-1 rounded-full"
                style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Description */}
        {session.description && (
          <div className="mb-5">
            <h2 className="text-base mb-2" style={{ color: 'var(--app-text)' }}>About this session</h2>
            <p className="text-sm leading-relaxed" style={{ color: 'var(--app-text-muted)' }}>
              {session.description}
            </p>
          </div>
        )}

        {/* Speakers */}
        {isEnabled('speakers') && speakers.length > 0 && (
          <div className="mb-5">
            <h2 className="text-base mb-3 flex items-center gap-2" style={{ color: 'var(--app-text)' }}>
              <Users className="w-4 h-4" style={{ color: ts.text }} />
              Speakers
            </h2>
            <div className="flex flex-col gap-2">
              {speakers.map((sp, i) => {
                const p = sp.profile ?? sp;
                const speakerId = p.id ?? String(i);
                const initials = (p.name ?? '?').charAt(0).toUpperCase();
                return (
                  <button
                    key={speakerId}
                    onClick={() => navigate(`/speaker/${speakerId}`)}
                    className="flex items-center gap-3 rounded-xl p-3 border text-left active:scale-[0.98] transition-transform"
                    style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}
                  >
                    <div
                      className="size-11 rounded-full flex items-center justify-center shrink-0 overflow-hidden text-sm font-medium"
                      style={{ background: ts.bg, color: ts.text }}
                    >
                      <AvatarImage source={p.avatar_url} alt={p.name} className="w-full h-full object-cover" fallback={initials} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm truncate" style={{ color: 'var(--app-text)' }}>{p.name}</p>
                      {(p.title || p.company) && (
                        <p className="text-xs truncate mt-0.5" style={{ color: 'var(--app-text-muted)' }}>
                          {[p.title, p.company].filter(Boolean).join(' · ')}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Linked Papers */}
        {isEnabled('papers') && papers.length > 0 && (
          <div className="mb-5">
            <h2 className="text-base mb-3 flex items-center gap-2" style={{ color: 'var(--app-text)' }}>
              <BookOpen className="w-4 h-4" style={{ color: ts.text }} />
              {session.type === 'poster' || session.type === 'talk' ? sessionLabel : 'Papers'}
            </h2>
            <div className="flex flex-col gap-2">
              {papers.map((paper, index) => <div key={paper.id}>
                {!selectedTopic && session.type === 'talk' && paper.presentation_topic && paper.presentation_topic !== papers[index - 1]?.presentation_topic && (
                  <h3 className="mt-3 mb-1 text-xs font-medium" style={{ color: ts.text }}>{paper.presentation_topic}</h3>
                )}
                <div className="flex items-stretch gap-2 rounded-xl p-3 border" style={{ background: 'var(--app-surface)', borderColor: 'var(--app-border)' }}>
                  <button onClick={() => navigate(`/paper/${paper.id}`)} className="flex min-w-0 flex-1 items-start gap-3 text-left active:scale-[0.98] transition-transform">
                    <FileText className="w-4 h-4 mt-0.5 shrink-0" style={{ color: ts.text }} />
                    <div className="flex-1 min-w-0">
                      {session.type === 'poster' && paper.paper_number && <p className="text-xs mb-0.5" style={{ color: ts.text }}>Poster {paper.paper_number}</p>}
                      <p className="text-sm" style={{ color: 'var(--app-text)' }}>{paper.title}</p>
                      {paper.presentation_start_time && paper.presentation_end_time && <p className="text-xs mt-0.5" style={{ color: ts.text }}>{fmt(paper.presentation_start_time)} – {fmt(paper.presentation_end_time)}</p>}
                      {paper.authors && paper.authors.length > 0 && <p className="text-xs mt-0.5 truncate" style={{ color: 'var(--app-text-muted)' }}>{paper.authors.slice(0, 3).join(', ')}{paper.authors.length > 3 ? ' et al.' : ''}</p>}
                    </div>
                  </button>
                  {(presentationFeedbackState === 'open' || presentationFeedbackState === 'upcoming') && <button aria-label={`Give feedback for ${paper.title}`} onClick={() => navigate(`/paper/${paper.id}/feedback`)} className="shrink-0 rounded-lg px-3 text-xs" style={{ background: 'var(--app-tag-bg)', color: 'var(--app-accent)' }}>Feedback</button>}
                </div>
              </div>)}
            </div>
          </div>
        )}

        {/* Actions */}
        {!preview && <div className="flex gap-3 mt-2">
          <button
            onClick={handleSave}
            className="flex-1 h-12 rounded-xl flex items-center justify-center gap-2 text-sm border transition-colors active:scale-[0.98]"
            style={saved
              ? { background: ts.bg, color: ts.text, borderColor: ts.accent }
              : { background: 'var(--app-surface)', color: 'var(--app-text)', borderColor: 'var(--app-border)' }
            }
          >
            <Bookmark className={`w-4 h-4 ${saved ? 'fill-current' : ''}`} />
            {saved ? 'Saved' : 'Save Session'}
          </button>
          {isEnabled('notes') && (
          <button
            onClick={() => navigate('/notes/new')}
            className="flex-1 h-12 rounded-xl flex items-center justify-center gap-2 text-sm border active:scale-[0.98] transition-transform"
            style={{ background: 'var(--app-surface)', color: 'var(--app-text)', borderColor: 'var(--app-border)' }}
          >
            <FileText className="w-4 h-4" />
            Take Notes
          </button>
          )}
        </div>}

        {prepAIEnabled && (
        <button
          onClick={() => navigate(`/session/${id}/prep`)}
          className="w-full mt-3 h-12 rounded-xl flex items-center justify-center gap-2 text-sm border active:scale-[0.98] transition-transform"
          style={{ background: 'var(--app-tag-bg)', borderColor: 'var(--app-tag-border)', color: 'var(--app-accent)' }}
        >
          <Zap className="w-4 h-4" />
          Prep with AI
        </button>
        )}
      </div>
      {!preview && <CopilotFab />}
    </div>
  );
}
